import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { FC, ReactNode, useEffect, useMemo, useState } from "react";

import {
  AppRole,
  type IUserImportSourceType,
  type IUserImportState,
  UserImportSourceType,
} from "@monorepo/core";

import AppHeader from "../components/app-header.component";
import CardImportDetailDrawer from "../components/card-import-detail-drawer.component";
import CardImportModal from "../components/card-import-modal.component";
import StatusChip from "../components/status-chip.component";
import useAuth from "../hooks/auth.hook";
import {
  useCardImportDetail,
  useCardImportHistory,
  useCreateCardImport,
} from "../hooks/card-import.hook";
import {
  formatCardImportBytes,
  formatCardImportTimestamp,
  getCardImportSourceLabel,
  isActiveImportStatus,
} from "../utils/card-import-display";
import { displayFontFamily } from "../theme/mtgTheme";

const getImportSourceLabel = (historyItem: {
  sourceType?: IUserImportSourceType;
  fileName?: string;
  sourceUrl?: string;
}) => getCardImportSourceLabel(historyItem);

const getImportMessage = (historyItem: {
  statusMessage?: string;
  error?: { message?: string } | null;
}) => historyItem.statusMessage ?? historyItem.error?.message;

const getImportSelectionId = (
  importState?: Pick<IUserImportState, "importId" | "pendingImportId">,
) => importState?.importId ?? importState?.pendingImportId;

const getImportMetaItems = (importState: {
  startedAt?: string;
  completedAt?: string;
  rowCount?: number;
  fileSizeBytes?: number;
}) =>
  [
    importState.startedAt
      ? `Started ${formatCardImportTimestamp(importState.startedAt, {
          longMonthDay: true,
        })}`
      : undefined,
    importState.completedAt
      ? `Completed ${formatCardImportTimestamp(importState.completedAt, {
          longMonthDay: true,
        })}`
      : undefined,
    importState.rowCount ? `${importState.rowCount} rows` : undefined,
    importState.fileSizeBytes
      ? formatCardImportBytes(importState.fileSizeBytes)
      : undefined,
  ].filter(Boolean);

const SummaryStatCard = ({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) => (
  <Card>
    <CardContent>
      <Typography
        variant="overline"
        sx={{ color: "text.secondary", letterSpacing: "0.12em" }}
      >
        {label}
      </Typography>
      <Box sx={{ mt: 1 }}>
        {typeof value === "string" || typeof value === "number" ? (
          <Typography variant="h5">{value}</Typography>
        ) : (
          value
        )}
      </Box>
    </CardContent>
  </Card>
);

const CardImportPage: FC = () => {
  const { viewRole } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImportId, setSelectedImportId] = useState<
    string | undefined
  >();
  const { createFileImport, isSubmitting } = useCreateCardImport();
  const historyQuery = useCardImportHistory();
  const history = historyQuery.data?.history ?? [];
  const activeImport = historyQuery.data?.activeImport;
  const activeImportId = getImportSelectionId(activeImport);
  const currentState = historyQuery.data?.currentState;
  const currentImportId = currentState?.importId;
  const hasActiveImport = isSubmitting || Boolean(activeImport);
  const detailQuery = useCardImportDetail(selectedImportId);
  const canViewDiagnostics = viewRole === AppRole.Admin;

  useEffect(() => {
    if (!selectedImportId) {
      return;
    }

    const exists = history.some(
      (historyItem) => historyItem.importId === selectedImportId,
    );
    if (
      !exists &&
      activeImportId !== selectedImportId &&
      currentImportId !== selectedImportId
    ) {
      setSelectedImportId(undefined);
    }
  }, [activeImportId, currentImportId, history, selectedImportId]);

  useEffect(() => {
    if (!hasActiveImport) {
      return;
    }

    const interval = window.setInterval(() => {
      void historyQuery.refetch();
    }, 3000);

    return () => {
      window.clearInterval(interval);
    };
  }, [hasActiveImport, historyQuery.refetch]);

  useEffect(() => {
    if (!isActiveImportStatus(detailQuery.data?.importState.status)) {
      return;
    }

    const interval = window.setInterval(() => {
      void detailQuery.refetch();
    }, 3000);

    return () => {
      window.clearInterval(interval);
    };
  }, [detailQuery.data?.importState.status, detailQuery.refetch]);

  const historySummary = useMemo(() => {
    return history.reduce(
      (summary, item) => {
        summary.total += 1;
        if (item.status === "COMMITTED") {
          summary.committed += 1;
        }
        if (item.status === "FAILED") {
          summary.failed += 1;
        }
        return summary;
      },
      {
        total: 0,
        committed: 0,
        failed: 0,
      },
    );
  }, [history]);

  const handleImportSubmit = async (file: File) => {
    const importState = await createFileImport(file);
    setSelectedImportId(getImportSelectionId(importState));
  };

  const renderImportItem = ({
    importItem,
    title,
  }: {
    importItem: IUserImportState;
    title: string;
  }) => {
    const importId = getImportSelectionId(importItem);
    const sourceLabel = getImportSourceLabel(importItem);
    const metaItems = getImportMetaItems(importItem);
    const importMessage = getImportMessage(importItem);

    return (
      <Card
        key={importId ?? importItem.updatedAt}
        sx={(theme) => ({
          borderColor:
            importId === selectedImportId
              ? theme.palette.primary.main
              : undefined,
          backgroundColor:
            importId === selectedImportId
              ? alpha(
                  theme.palette.primary.main,
                  theme.palette.mode === "dark" ? 0.14 : 0.08,
                )
              : undefined,
          transition: "border-color 120ms ease, background-color 120ms ease",
        })}
      >
        <CardActionArea
          disabled={!importId}
          onClick={() => {
            if (importId) {
              setSelectedImportId(importId);
            }
          }}
        >
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ md: "flex-start" }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ fontFamily: displayFontFamily }}
                  >
                    {title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, wordBreak: "break-word" }}
                  >
                    {sourceLabel}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {importItem.sourceType ? (
                    <Chip
                      label={
                        importItem.sourceType === UserImportSourceType.UPLOAD
                          ? "Upload"
                          : "URL"
                      }
                      size="small"
                      variant="outlined"
                    />
                  ) : null}
                  <StatusChip status={importItem.status} />
                </Stack>
              </Stack>

              {metaItems.length > 0 ? (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {metaItems.map((metaItem) => (
                    <Chip
                      key={metaItem}
                      label={metaItem}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              ) : null}

              {importMessage ? (
                <Typography variant="body2" color="text.secondary">
                  {importMessage}
                </Typography>
              ) : null}
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  return (
    <Box sx={{ minHeight: "100vh", pb: 6 }}>
      <AppHeader />

      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
        <Stack spacing={3}>
          <Card>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Stack
                direction={{ xs: "column", lg: "row" }}
                spacing={3}
                justifyContent="space-between"
                alignItems={{ lg: "center" }}
              >
                <Box>
                  <Typography variant="h3">Card Imports</Typography>
                  <Typography
                    color="text.secondary"
                    sx={{
                      mt: 1.5,
                      maxWidth: 720,
                      fontFamily: displayFontFamily,
                      fontSize: "0.95rem",
                    }}
                  >
                    Upload ManaBox CSVs
                  </Typography>
                </Box>

                <Button
                  onClick={() => setIsModalOpen(true)}
                  disabled={hasActiveImport}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : undefined
                  }
                >
                  {isSubmitting ? "Submitting..." : "Import cards"}
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Stack spacing={3}>
                <Typography variant="h4">Import Overview</Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      xl: "repeat(4, minmax(0, 1fr))",
                    },
                    gap: 2,
                  }}
                >
                  <SummaryStatCard
                    label="Current state"
                    value={
                      currentState?.status ? (
                        <StatusChip
                          status={currentState.status}
                          size="medium"
                        />
                      ) : (
                        "Idle"
                      )
                    }
                  />
                  <SummaryStatCard
                    label="History entries"
                    value={historySummary.total}
                  />
                  <SummaryStatCard
                    label="Active"
                    value={activeImport ? 1 : 0}
                  />
                  <SummaryStatCard
                    label="Committed"
                    value={historySummary.committed}
                  />
                  <SummaryStatCard
                    label="Source"
                    value={currentState?.sourceType ?? "N/A"}
                  />
                  <SummaryStatCard
                    label="Rows"
                    value={currentState?.rowCount ?? "N/A"}
                  />
                  <SummaryStatCard
                    label="Unique cards"
                    value={currentState?.uniqueCardCount ?? "N/A"}
                  />
                  <SummaryStatCard
                    label="File size"
                    value={formatCardImportBytes(currentState?.fileSizeBytes)}
                  />
                </Box>

                {canViewDiagnostics ? (
                  <Box
                    component="pre"
                    sx={(theme) => ({
                      m: 0,
                      p: 2,
                      borderRadius: 2,
                      overflow: "auto",
                      fontSize: 12,
                      lineHeight: 1.6,
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#0D1117" : "#F7F4ED",
                      border: "1px solid",
                      borderColor: "divider",
                    })}
                  >
                    {JSON.stringify(currentState ?? null, null, 2)}
                  </Box>
                ) : null}
              </Stack>
            </CardContent>
          </Card>

          {activeImport ? (
            <Card>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Stack spacing={2.5}>
                  <Typography variant="h4">Current Run</Typography>

                  {renderImportItem({
                    importItem: activeImport,
                    title: canViewDiagnostics
                      ? activeImportId ?? "Unknown import"
                      : "Current import",
                  })}
                </Stack>
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Stack spacing={2.5}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ md: "center" }}
                >
                  <Typography variant="h4">Import History</Typography>
                  <Button
                    variant="outlined"
                    onClick={() => historyQuery.refetch()}
                  >
                    Refresh
                  </Button>
                </Stack>

                {historyQuery.isLoading ? (
                  <Alert severity="info">Loading import history...</Alert>
                ) : null}

                {historyQuery.error ? (
                  <Alert severity="error">{historyQuery.error.message}</Alert>
                ) : null}

                {!historyQuery.isLoading &&
                !historyQuery.error &&
                history.length === 0 ? (
                  <Alert severity="info">
                    {activeImport
                      ? "No completed or failed imports yet."
                      : "No imports yet. Start with a CSV upload."}
                  </Alert>
                ) : null}

                <Stack spacing={1.5}>
                  {history.map((historyItem) => {
                    const historyTitle = canViewDiagnostics
                      ? historyItem.importId ?? "Unknown import"
                      : historyItem.sourceType === UserImportSourceType.UPLOAD
                        ? "Uploaded CSV"
                        : historyItem.sourceType === UserImportSourceType.URL
                          ? "Legacy URL import"
                          : "Import";
                    return renderImportItem({
                      importItem: historyItem,
                      title: historyTitle,
                    });
                  })}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Container>

      <CardImportModal
        isOpen={isModalOpen}
        isSubmitting={isSubmitting}
        onClose={() => {
          if (isSubmitting) {
            return;
          }
          setIsModalOpen(false);
        }}
        onSubmit={handleImportSubmit}
      />

      <CardImportDetailDrawer
        importId={selectedImportId}
        detail={detailQuery.data}
        isLoading={detailQuery.isLoading}
        error={detailQuery.error ?? null}
        onClose={() => setSelectedImportId(undefined)}
      />
    </Box>
  );
};

export default CardImportPage;
